import {
  call,
  put,
  takeLatest,
  fork,
  takeEvery,
  select,
} from 'redux-saga/effects';
import agencyServiceApi from 'services/api/AgencyManagementApi';
import handleError from 'services/error/handleScopeError';
import { communicationStatus, HttpStatus } from 'services/utils';
import filtersSelector from 'store/filters/selectors';
import { loggerActions } from 'store/logger';
import commonActions from 'store/common/actions';
import loggerConstants from 'store/logger/constant';
import sharedSagas from 'store/sharedSagas';
import uploadActions from 'store/upload/actions';

import { convertDataDetailResponse } from 'services/agency-management/agencyManagementService';
import { STEP_TWO } from 'domain/agency-management/consts';
import { CONTENT_TYPE } from 'domain/consts';

import actions from './actions';
import types from './types';
import selectors from './selectors';

const { BAD_REQUEST } = HttpStatus;
const { LOADING, SUCCEEDED, FAILED } = communicationStatus;

function* errorHandler(err) {
  const errors = err.response
    ? err.response.data.errors
    : [{ message: err.message }];

  yield put(loggerActions.logError(errors, loggerConstants.LOG_LEVEL_ERROR));
}

function* handleGetList() {
  try {
    yield put(actions.setStatusList(LOADING));
    const filters = yield select(filtersSelector.getForApi);
    const sort = yield select(selectors.getSortRequest);
    const query = { filters };
    if (sort !== undefined) query.sort = sort;
    const { data } = yield call(agencyServiceApi.getList, query);
    yield put(actions.setDataList(data));
  } catch (e) {
    yield fork(errorHandler, e);
    yield put(actions.setStatusList(FAILED));
  }
}

function* handleGetDetail(action) {
  const { id, callback, convert } = action.payload;
  try {
    yield put(actions.setStatusDetail(LOADING));
    const {
      data: { data },
    } = yield call(agencyServiceApi.getDetail, id, convert);
    yield put(actions.setDetail(convertDataDetailResponse(data)));
    yield put(actions.setStatusDetail(SUCCEEDED));
  } catch (e) {
    const { error: response, scope } = handleError(e?.response || {}, [
      HttpStatus.BAD_REQUEST,
    ]);
    const errors = response?.data?.errors || [];
    yield put(actions.setErrorDetail(errors, scope));
    yield put(actions.setStatusDetail(FAILED));
    callback(true, errors);
  }
}

function* handleValidate(action) {
  const { data, changeStep, callback } = action.payload;
  try {
    yield put(actions.setStatusDetail(LOADING));
    const { data: dataResponse } = yield call(agencyServiceApi.validate, data);
    yield put(actions.setStatusDetail(SUCCEEDED));
    if (dataResponse.errors && dataResponse.errors.length > 0) {
      callback(true, dataResponse.errors);
    } else {
      changeStep(STEP_TWO);
    }
  } catch (e) {
    const { error: response, scope } = handleError(e?.response || {}, [
      HttpStatus.BAD_REQUEST,
    ]);
    const errors = response?.data?.errors || [];
    yield put(actions.setErrorDetail(errors, scope));
    callback(true, errors);
  }
}

function* handleCreate(action) {
  const { data, callback } = action.payload;
  try {
    yield put(actions.setStatusDetail(LOADING));
    yield call(agencyServiceApi.create, data);
    yield put(actions.setStatusDetail(SUCCEEDED));
    callback();
    yield fork(handleGetList);
  } catch (e) {
    const { error: response, scope } = handleError(e?.response || {}, [
      HttpStatus.BAD_REQUEST,
    ]);
    const errors = response?.data?.errors || [];
    yield put(actions.setErrorDetail(errors, scope));
    callback(true, errors);
  }
}

function* handleUpdate(action) {
  const { id, data, callback } = action.payload;
  try {
    yield put(actions.setStatusDetail(LOADING));
    yield call(agencyServiceApi.update, id, data);
    yield put(actions.setStatusDetail(SUCCEEDED));
    callback();
    yield fork(handleGetList);
  } catch (e) {
    const { error: response, scope } = handleError(e?.response || {}, [
      HttpStatus.BAD_REQUEST,
    ]);
    const errors = response?.data?.errors || [];
    yield put(actions.setErrorDetail(errors, scope));
    callback(true, errors);
  }
}

function* handleDelete(action) {
  const { ids, callback } = action.payload;
  try {
    yield call(agencyServiceApi.delete, ids);
    yield put(actions.setStatusList(SUCCEEDED));
    callback();
  } catch (e) {
    const { error, scope } = handleError(e?.response || {}, [BAD_REQUEST]);
    const errors = error?.data?.errors || [];

    yield put(actions.setErrorList(errors, scope));
    callback(true, errors);
  }
}

function* handleGetAgent(action) {
  try {
    yield put(actions.setStatus(action.type, LOADING));
    const { data } = yield call(agencyServiceApi.getAgent);
    yield put(actions.setAgent(data));
  } catch (e) {
    const { error, scope } = handleError(e?.response || {}, [BAD_REQUEST]);
    const errors = error?.data?.errors || [];

    yield put(actions.setErrors(action.type, errors, scope));
  }
}

function* handleSyncAgent(action) {
  const { userIds, callback } = action.payload;
  try {
    yield put(actions.setStatus(action.type, LOADING));
    const { data } = yield call(agencyServiceApi.syncAgent, userIds);
    callback(data);
    yield put(actions.setStatus(action.type, SUCCEEDED));
  } catch (e) {
    const { error, scope } = handleError(e?.response || {}, [BAD_REQUEST]);
    const errors = error?.data?.errors || [];
    callback(e, true);
    yield put(actions.setErrors(action.type, errors, scope));
  }
}

function* handleDownloadCsvAgent(action) {
  try {
    yield put(
      commonActions.setDownloadNotify('CSVファイルを作成しています...')
    );
    yield call(agencyServiceApi.downloadCsvAgent);
    yield put(commonActions.setDownloadNotify(''));
  } catch (e) {
    const { error, scope } = handleError(e?.response || {});
    const errors = error?.data?.errors || [];

    yield put(actions.setErrors(action.type, errors, scope));
  }
}

function* handleDownloadCsvAdAgent(action) {
  const { data } = action.payload;
  try {
    yield put(
      commonActions.setDownloadNotify('CSVファイルを作成しています...')
    );
    yield call(agencyServiceApi.downloadCsvAdAgent, data);
    yield put(commonActions.setDownloadNotify(''));
  } catch (e) {
    const { error, scope } = handleError(e?.response || {});
    const errors = error?.data?.errors || [];

    yield put(actions.setErrors(action.type, errors, scope));
  }
}

function* handleUploadCsv(action) {
  try {
    const { screenId, file } = action.payload;

    yield put(uploadActions.requestStart(screenId));
    const { uploadUrl, fileName } = yield call(
      agencyServiceApi.getUploadUrl,
      CONTENT_TYPE.CSV
    );

    yield call(agencyServiceApi.uploadFile, file, uploadUrl, CONTENT_TYPE.CSV);
    yield call(agencyServiceApi.submitCsv, fileName);
    yield put(uploadActions.uploadSucceeded());
    yield put(actions.setUploadStatus(SUCCEEDED));
    yield put(actions.setStatusOpenCompletedModal(true));
    yield put(actions.getList());
  } catch (error) {
    const { error: response } = handleError(error.response, [
      HttpStatus.BAD_REQUEST,
    ]);
    const errors = response?.data?.errors || [];
    const status = response?.status ?? 500;

    if (status === 400) {
      // validate error
      yield put(uploadActions.requestFailed(errors));
    } else {
      // api or app error
      yield put(uploadActions.requestError(handleError(error)));
    }
  }
}
function* handleDownloadCsvAgencyManagement() {
  try {
    const filters = yield select(filtersSelector.getForApi);
    yield put(
      commonActions.setDownloadNotify('CSVファイルを作成しています...')
    );
    yield call(agencyServiceApi.downloadCsvAgencyManagement, filters);
    yield put(commonActions.setDownloadNotify('CSVファイルを作成しました'));
  } catch (error) {
    yield put(loggerActions.logDownloadCsvError());
    yield put(commonActions.setDownloadNotify(''));
  }
}

export default function* watch() {
  yield takeLatest(
    types.GET_LIST,
    sharedSagas.safe(errorHandler, handleGetList)
  );
  yield takeLatest(
    types.GET_DETAIL,
    sharedSagas.safe(errorHandler, handleGetDetail)
  );
  yield takeEvery(
    types.VALIDATE,
    sharedSagas.safe(errorHandler, handleValidate)
  );
  yield takeEvery(types.CREATE, sharedSagas.safe(errorHandler, handleCreate));
  yield takeEvery(types.UPDATE, sharedSagas.safe(errorHandler, handleUpdate));
  yield takeLatest(types.DELETE, sharedSagas.safe(errorHandler, handleDelete));
  yield takeLatest(
    types.GET_AGENT,
    sharedSagas.safe(errorHandler, handleGetAgent)
  );
  yield takeLatest(
    types.SYNC_AGENT,
    sharedSagas.safe(errorHandler, handleSyncAgent)
  );
  yield takeLatest(
    types.DOWNLOAD_CSV_AGENT,
    sharedSagas.safe(errorHandler, handleDownloadCsvAgent)
  );
  yield takeLatest(
    types.DOWNLOAD_CSV_AGENCY_MANAGEMENT,
    sharedSagas.safe(errorHandler, handleDownloadCsvAgencyManagement)
  );
  yield takeLatest(
    types.DOWNLOAD_CSV_AD_AGENT,
    sharedSagas.safe(errorHandler, handleDownloadCsvAdAgent)
  );
  yield takeLatest(types.UPLOAD_CSV, handleUploadCsv);
}
